import { observable, action, IObservableArray, computed } from "mobx";
import SeekerStore from "./seekerStore";
import { hashHistory } from "react-router";
import { Organization } from "../models/organization";
import { Category } from "../models/category";
import { OpportunityViewState, RefType, PrereqType, OperatorType, PositionLevel, LookupType, ApplicationProcess, SavedOpportunityState, BusyEvent } from "../models/enums";
import { OpportunitySummary, IOpportunityResponse, Prereq, PrereqField, SetSkill, CategoryOps, Opportunity, ClientCache, ItemVersion, RecommendedOpRequest } from "../models/opportunity";
import { FilterEntry, FilterRules, FilterDisplay } from "../models/filteroption";
import { IOption } from "../models/localization";
import { LookupValue } from "../models/lookup";
import { Profile, SavedOpportunity, EducationExperience, CCLCExperience, SkillFit } from "../models/profile";
import Application from "../models/application";
import Fetcher, { IHttpResult } from "../utils/fetcher";
import { Auth } from "../utils/auth";
import { copyArray, sort } from "../utils/data";
import _difference from "lodash";


export default class OpportunityStore {
  private _store: SeekerStore;
  // Reference data
  @observable organizations: Array<Organization>;
  @observable categories: Array<Category>;
  @observable categoryOps: Array<CategoryOps>;
  @observable opportunities: Array<Opportunity>;
  @observable relatedOps: Array<OpportunitySummary>;
  @observable featuredOps: Array<OpportunitySummary>;
  @observable recommendedOps: Array<OpportunitySummary>;
  @observable filterAges: Array<number>;
  @observable selectedOp: Opportunity;
  @observable generatingProfile: boolean;
  @observable viewState: number;
  // Advanced Search data
  @observable searching: boolean;
  @observable filterEntries: Array<FilterEntry>;
  @observable filterRules: FilterRules;
  @observable filterResults: Array<OpportunitySummary>;
  @observable showFilterControls: boolean;
  @observable searchTriggered: boolean;
  // Application
  @observable application: Application;
  @observable selectedSetSkill: SetSkill;
  @observable updateReference: string;

  constructor(store: SeekerStore) {
    this._store = store;
    this.initOrganizations();
    this.initCategories();
    this.categoryOps = new Array<CategoryOps>();
    this.opportunities = new Array<Opportunity>();
    this.relatedOps = new Array<OpportunitySummary>();
    this.featuredOps = new Array<OpportunitySummary>();
    this.recommendedOps = new Array<OpportunitySummary>();
    this.filterAges = new Array<number>();
    this.selectedOp = null;
    this._store.languageCallbacks.push(this.languageReset);
    this.viewState = OpportunityViewState.NotSelected;
    this.searching = false;
    this.filterEntries = new Array<FilterEntry>();
    this.filterResults = new Array<OpportunitySummary>();
    this.filterRules = new FilterRules();
    this.showFilterControls = false;
    this.setFilterZip();
    this.initFilterAges();
    this.selectedSetSkill = null;
    this.updateReference = "updateSection";
    this.searchTriggered = false;
  }
  @action clear(): void {
    this.languageReset(); // trigger reload of orgs and categories - in case the site content has changed.
    this.filterEntries = new Array<FilterEntry>();
    this.filterResults = new Array<OpportunitySummary>();
    this.filterRules = new FilterRules();
    this.showFilterControls = false;
    this.selectedSetSkill = null;
    this.opportunities = new Array<Opportunity>();
    this.relatedOps = new Array<OpportunitySummary>();
    this.featuredOps = new Array<OpportunitySummary>();
    this.recommendedOps = new Array<OpportunitySummary>();
  }
  @action setSelectedSetSkill = (skill: SetSkill): void => {
    this.selectedSetSkill = skill;
  }
  clearSelectedSetSkill = (): void => {
    this.selectedSetSkill = null;
  }
  @action setViewState = (n: OpportunityViewState): void => {
    this.viewState = n;
  }
  @action languageReset = (): void => {
    this.categories.map((cat: Category): void => {
      localStorage.removeItem(cat.idHash);
    });
    this.initOrganizations();
    this.initCategories();
    (this.opportunities as IObservableArray<Opportunity>).clear();
    (this.featuredOps as IObservableArray<OpportunitySummary>).clear();
    (this.relatedOps as IObservableArray<OpportunitySummary>).clear();
    (this.filterEntries as IObservableArray<FilterEntry>).clear();
    (this.recommendedOps as IObservableArray<OpportunitySummary>).clear();
    (this.categoryOps as IObservableArray<CategoryOps>).clear();
    let newOps: Array<Opportunity> = new Array<Opportunity>();
    this.opportunities.map((op: Opportunity): void => {
      Fetcher.get(`api/opportunitity/${op.idHash}`, action((op: Opportunity): void => {
        newOps.push(new Opportunity(op));
      }));
    });
    (this.opportunities as IObservableArray<Opportunity>).replace(newOps);

    this.clearSearch();
  }
  @action initOrganizations(): void {
    this.organizations = new Array<Organization>();
    this._store.viewState.setBusyBit(BusyEvent.LoadCategories);
    Fetcher.get("api/organizations/seeker", action((orgs: Array<Organization>): void => {
      copyArray(orgs, this.organizations, (o) => { return new Organization(o) });
      this._store.viewState.clearBusyBit(BusyEvent.LoadCategories);
    }));
  }
  @action initCategories(): void {
    this.categories = new Array<Category>();
    Fetcher.get("api/categories/seeker", action((cats: Array<Category>): void => {
      copyArray(cats, this.categories, (c) => { return new Category(c) });
    }));
  }
  initFilterAges(): void {
    Fetcher.get("api/opportunities/filter-ages", action((ages: Array<number>): void => {
      (this.filterAges as IObservableArray<number>).replace(ages);
    }));
  }
  @action initCategoryOps = (catId: string): void => {
    if (!catId) {
      return;
    }
    this._store.viewState.setBusyBit(BusyEvent.LoadCategoryOps);
    if (!this.categoryOps[catId]) {
      this.categoryOps[catId] = new CategoryOps(catId);
      const clientCache: ClientCache = new ClientCache();
      const cachedOpStr: string = null; // localStorage.getItem(catId);
      if (cachedOpStr) {
        const cachedOps: OpportunitySummary[] = JSON.parse(cachedOpStr);
        this.categoryOps[catId].updateOpportunities({ deadOps: [], opportunities: cachedOps });
        cachedOps.map((os: OpportunitySummary): void => {
          clientCache.addItem(os.idHash, os.version);
        });
      }
      Fetcher.postWithData(`api/opportunities/${catId}`, clientCache, action((resp: IOpportunityResponse): void => {
        this.categoryOps[catId].updateOpportunities(resp);
        //localStorage.setItem(catId, JSON.stringify(resp.opportunities));
        this._store.viewState.clearBusyBit(BusyEvent.LoadCategoryOps);
      }));
    }
  }
  getRelatedOpportunities = (opId: string): void => {
    this._store.viewState.setBusyBit(BusyEvent.LoadRelatedOps);
    Fetcher.get(`api/opportunities/related?opportunity=${opId}`, action((ops: Array<OpportunitySummary>): void => {
      if (ops) {
        copyArray(ops, this.relatedOps, (o: OpportunitySummary) => { return new OpportunitySummary(o); });
      } else {
        (this.relatedOps as IObservableArray<OpportunitySummary>).clear();
      }
      this._store.viewState.clearBusyBit(BusyEvent.LoadRelatedOps);
    }));
  }
  getFeaturedOps = (): void => {
    if (this.featuredOps.length === 0) {
      this._store.viewState.setBusyBit(BusyEvent.LoadFeaturedOps);
      Fetcher.get(`api/opportunities/featured?max=6`, action((ops: Array<OpportunitySummary>): void => {
        if (ops) {
          copyArray(ops, this.featuredOps, (o: OpportunitySummary) => { return new OpportunitySummary(o); });
        } else {
          (this.featuredOps as IObservableArray<OpportunitySummary>).clear();
        }
        this._store.viewState.clearBusyBit(BusyEvent.LoadFeaturedOps);
      }));
    }
  }
  @action selectOpportunity(opId: string, track: boolean = false): void {
    const ops: Opportunity[] = this.opportunities.filter((o: Opportunity): boolean => {
      return o.idHash === opId;
    });
    if (ops.length > 0) {
      this.selectedOp = ops[0];
      if (track) {
        this._store.profileStore.trackOpportunity(this.selectedOp);
      }
    } else {
      Fetcher.get(`api/opportunity/${opId}`, action((op: Opportunity): void => {
        const op2: Opportunity = new Opportunity(op);
        this.opportunities.push(op2);
        this.selectedOp = op2;
        if (track) {
          this._store.profileStore.trackOpportunity(this.selectedOp);
        }
      }));
    }
  }
  loadOps = (savedOps: SavedOpportunity[], cb: () => void): void => {
    let opCt: number = savedOps.length;
    if (opCt === 0) {
      cb();
      this._store.viewState.clearBusyBit(BusyEvent.LoadOpportunity);
    }
    this._store.viewState.setBusyBit(BusyEvent.LoadOpportunity);
    savedOps.map((so: SavedOpportunity): void => {
      Fetcher.get(`/api/opportunity/${so.opportunityId}`, action((op: Opportunity): void => {
        opCt--;
        if (op) {
          this.opportunities.push(new Opportunity(op));
        } else {
          (this._store.profileStore.profile.savedOps as IObservableArray<SavedOpportunity>).remove(so);
        }
        if (opCt === 0) {
          cb();
          this._store.viewState.clearBusyBit(BusyEvent.LoadOpportunity);
        }
      }));
    });
  }
  getCategory = (catId: string): Category => {
    return this.categories.find((c: Category): boolean => {
      return c.idHash === catId;
    });
  }
  getOpportunity(opId: string): Opportunity {
    let op: Opportunity = this.opportunities.find((o: Opportunity): boolean => {
      return o.idHash === opId;
    });
    if (op) {
      return op;
    } else {
      this._store.viewState.setBusyBit(BusyEvent.LoadOpportunity);
      const x = Fetcher.getAsync(`/api/opportunity/${opId}`).then((data: Opportunity): any => {
        if (data) {
          op = new Opportunity(data);
          this.opportunities.push(op);
          this._store.viewState.clearBusyBit(BusyEvent.LoadOpportunity);
        }
        return op;
      });
    }
  }
  @action clearOpportunity = (): void => {
    this.selectedOp = null;
  }
  prereqFulfilled = (prereq: Prereq): boolean => {
    const profile: Profile = this._store.profileStore.profile;
    if (!profile) {
      return false;
    }
    let stack: boolean[] = [];
    let fulfilled: boolean = false;
    prereq.fields.map((fld: PrereqField, i: number): void => {
      if (fld.opType === OperatorType.FIELD) {
        switch (fld.type) {
          case PrereqType.Education:
            stack.push(profile.educations.findIndex((ee: EducationExperience): boolean => {
              return ee.degTypeId >= fld.refVal && ee.grad;
            }) !== -1);
            break;
          case PrereqType.CCLC:
            stack.push(profile.cclcs.findIndex((ce: CCLCExperience): boolean => {
              return ce.cclcId === fld.refVal && !ce.expired;
            }) !== -1);
            break;
          case PrereqType.Age:
            stack.push(profile.demographics.age >= fld.refVal);
            break;
          case PrereqType.Acceptance:
            stack.push(profile.selfCerts.indexOf(fld.refVal) !== -1);
            break;
        }
      } else if (fld.opType === OperatorType.OR) {
        if (stack.length >= 2) {
          const a: boolean = stack.pop();
          const b: boolean = stack.pop();
          stack.push(a || b);
        } else {
          stack.push(false);
        }
      } else if (fld.opType === OperatorType.AND) {
        if (stack.length >= 2) {
          const a: boolean = stack.pop();
          const b: boolean = stack.pop();
          stack.push(a && b);
        } else {
          stack.push(false);
        }
      }
    });
    return stack.length > 0 ? stack[0] : false;
  }
  prereqFulfilledLabel = (prereq: Prereq): string => {
    const profile: Profile = this._store.profileStore.profile;
    let lbl: string = "";
    if (!profile) {
      return lbl;
    }
    prereq.fields.map((pf: PrereqField): void => {
      switch (pf.type) {
        case PrereqType.Education:
          const ed: EducationExperience = profile.educations.find((ee: EducationExperience): boolean => { return ee.degTypeId >= pf.refVal && ee.grad });
          if (ed && lbl.length === 0) {
            lbl = `${this._store.localization.fields.prereq.fulfilled} ${ed.name}`;
          }
          break;
        case PrereqType.CCLC:
          const cclc: CCLCExperience = profile.cclcs.find((ce: CCLCExperience): boolean => { return ce.cclcId === pf.refVal && !ce.expired });
          if (cclc && lbl.length === 0) {
            lbl = `${this._store.localization.fields.prereq.fulfilled} ${cclc.title}`;
          }
          break;
      }
    });
    return lbl;
  }
  generateProfile = (opId: string): void => {
    const op: Opportunity = this.getOpportunity(opId);
    if (op != null) {
      const savedOp = this._store.profileStore.getSavedOpportunity(opId);
      this._store.viewState.setBusyBit(BusyEvent.GeneratingProfile);
      let data: Application = new Application();
      data.mapFromProfile(this._store.profileStore.profile.demographics);
      op.requiredSkills().map((sk: SetSkill): void => {
        data.scores.push({ skill: sk.skillId, score: savedOp.getScore(sk.skillId) });
      });
      op.desiredSkills().map((sk: SetSkill): void => {
        data.scores.push({ skill: sk.skillId, score: savedOp.getScore(sk.skillId) });
      });

      Fetcher.postFileDownload(`api/opportunities/${op.idHash}/profile/pdf`, data, (): void => {
        this._store.viewState.clearBusyBit(BusyEvent.GeneratingProfile);
      });
    }
  }
  // Opportunity Search
  @action setShowFilterControls = (show: boolean): void => {
    this.showFilterControls = show;
  }
  getFilteredMeta(cb?: () => void): void {
    if (this.filterEntries.length > 0) {
      return;
    }
    const zip: string = this.filterRules.zip.length !== 5 ? "00000" : this.filterRules.zip;
    Fetcher.get(`api/opportunities/filter-meta?zip=${zip}`, action((meta: Array<FilterEntry>): void => {
      copyArray(meta, this.filterEntries, (fe: FilterEntry) => { return new FilterEntry(fe); });
      if (cb) {
        cb();
      }
    }));
  }
  @action getFilteredResults = (): void => {
    this._store.viewState.setBusyBit(BusyEvent.SearchingOpportunities);
    (this.filterResults as IObservableArray<OpportunitySummary>).clear();
    const missingSummaries: Array<string> = new Array<string>();
    const tempOps: Array<OpportunitySummary> = new Array<OpportunitySummary>();
    this.filterEntries.filter((fe: FilterEntry): boolean => { return fe.active; }).map((fe: FilterEntry): void => {
      let op: OpportunitySummary = null;
      this.categoryOps.map((co: CategoryOps): void => {
        if (op !== null) { return; }
        op = co.opportunities.find((o: OpportunitySummary): boolean => { return o.idHash === fe.jobId; });
      });
      if (!op) {
        missingSummaries.push(fe.jobId);
      } else {
        tempOps.push(op);
      }
    });
    this._store.viewState.clearBusyBit(BusyEvent.SearchingOpportunities);
    if (missingSummaries.length === 0) {
      (this.filterResults as IObservableArray<OpportunitySummary>).replace(sort(tempOps, "name"));
    } else {
      this._store.viewState.setBusyBit(BusyEvent.SearchingOpportunities);
      Fetcher.postWithData("api/opportunities/summaries", missingSummaries, action((resp: Array<OpportunitySummary>): void => {
        resp.map((o: OpportunitySummary): void => {
          tempOps.push(o);
        });
        (this.filterResults as IObservableArray<OpportunitySummary>).replace(sort(tempOps, "name"));
        this._store.viewState.clearBusyBit(BusyEvent.SearchingOpportunities);
      }));
    }
    this.searchTriggered = true;
  }
  @action clearSearch(): void {
    (this.filterResults as IObservableArray<OpportunitySummary>).clear();
    this.filterEntries.map((fe: FilterEntry): void => {
      fe.active = true;
    });
    this.filterRules = new FilterRules();
    if (this._store.profileStore.profile) {
      this.filterRules.zip = this._store.profileStore.profile.demographics.address.zip;
    }
    this.searchTriggered = false;
  }
  @action setFilterZip = (): void => {
    const p: Profile = this._store.profileStore.profile;
    if (p && p.demographics && p.demographics.address) {
      if (this.filterRules.zip !== p.demographics.address.zip) {
        this.filterRules.zip = p.demographics.address.zip;
        (this.filterEntries as IObservableArray<FilterEntry>).clear();
        this.getFilteredMeta(this.updateFilters);
      }
    }
  }
  @action setFilterPosition = (title: string): void => {
    this.filterRules.position = title;
    this.updateFilters();
    //if 'no matches' is displayed and the user clears the search field then 'no matches' should be removed
    if (this.filterRules.position === "" && this.searchTriggered && this.filterResults.length === 0) {
      this.searchTriggered = false;
    }
  }
  @action setFilterOrg = (id: string, add: boolean): void => {
    if (add) {
      this.filterRules.orgs.push(id);
    } else {
      (this.filterRules.orgs as IObservableArray<string>).remove(id);
    }
    this.updateFilters();
  }
  @action setFilterCategory = (id: string, add: boolean): void => {
    if (add) {
      this.filterRules.categories.push(id);
    } else {
      (this.filterRules.categories as IObservableArray<string>).remove(id);
    }
    this.updateFilters();
  }
  @action setFilterZipcode = (zip: string): void => {
    this.filterRules.zip = zip;
    if (zip.length === 5 || zip.length === 0) {
      (this.filterEntries as IObservableArray<FilterEntry>).clear();
      this.getFilteredMeta(this.updateFilters);
    }
  }
  @action setFilterLevel = (lvl: PositionLevel, add: boolean): void => {
    if (add) {
      this.filterRules.levels.push(lvl);
    } else {
      (this.filterRules.levels as IObservableArray<PositionLevel>).remove(lvl);
    }
    this.updateFilters();
  }
  @action setFilterEmploymentLevel = (lvl: number, add: boolean): void => {
    if (add) {
      this.filterRules.empTypes.push(lvl);
    } else {
      (this.filterRules.empTypes as IObservableArray<number>).remove(lvl);
    }
    this.updateFilters();
  }
  @action setFilterMinDistance = (dist: number): void => {
    this.filterRules.distance = dist;
    this.updateFilters();
  }
  @action setFilterCanApply = (ca: boolean): void => {
    this.filterRules.canApply = ca;
    this.updateFilters();
  }
  @action setFilterMinEdLevel = (lvl: number): void => {
    this.filterRules.minEd = lvl;
    this.updateFilters();
  }
  @action setFilterMinAge = (age: number): void => {
    this.filterRules.minAge = age;
    this.updateFilters();
  }
  @action private updateFilters = (): void => {
    this.filterEntries.map((fe: FilterEntry): void => {
      const orgId: string = `${fe.orgId}-${fe.locationId}`;
      const orgNdx: number = this.filterRules.orgs.length > 0 ? this.filterRules.orgs.indexOf(orgId) : 0;
      const catNdx: number = this.filterRules.categories.length > 0 ? this.filterRules.categories.indexOf(fe.categoryId) : 0;
      const levelNdx: number = this.filterRules.levels.length > 0 ? this.filterRules.levels.indexOf(fe.level) : 0;
      const expNdx: number = this.filterRules.empTypes.length > 0 ? this.filterRules.empTypes.indexOf(fe.employmentType) : 0;
      const applyValid: boolean = this.filterRules.canApply ? fe.canApply : true;
      const ageValid: boolean = fe.minAge <= this.filterRules.minAge;
      const edValid: boolean = fe.minEd >= this.filterRules.minEd;

      let distValid: boolean = true;
      switch (this.filterRules.distance) {
        case 5:
          distValid = fe.ltE5;
          break;
        case 10:
          distValid = fe.ltE10;
          break;
        case 30:
          distValid = fe.ltE30;
          break;
        case 50:
          distValid = fe.ltE50;
          break;
      }
      const plc: string = this.filterRules.position.toLowerCase();
      let nameValid: boolean = this.filterRules.position.length > 0 ? fe.jobTitle.toLowerCase().indexOf(plc) !== -1 : true;
      if (!nameValid) {
        fe.keywords.map((kw: string): void => {
          if (nameValid) { return; }
          nameValid = kw.toLowerCase().indexOf(plc) !== -1;
        });
      }
      if (!nameValid) {
        fe.altTitles.map((t: string): void => {
          if (nameValid) { return; }
          nameValid = t.toLowerCase().indexOf(plc) !== -1;
        });
      }
      fe.active = orgNdx >= 0 && catNdx >= 0 && levelNdx >= 0 && expNdx >= 0 && distValid && applyValid && ageValid && edValid && nameValid;
    });
  }
  @computed get filteredOrgs(): Array<FilterDisplay> {
    const orgs: Array<FilterDisplay> = new Array<FilterDisplay>();
    let activeCt: number = 0;
    this.filterEntries.map((fe: FilterEntry): void => {
      const id: string = `${fe.orgId}-${fe.locationId}`;
      let fd: FilterDisplay = orgs.find((fd: FilterDisplay): boolean => { return fd.value === id });
      if (!fd) {
        fd = new FilterDisplay(fe.orgName, id);
        fd.checked = this.filterRules.orgs.length > 0 ? this.filterRules.orgs.indexOf(id) !== -1 : false;
        orgs.push(fd);
      }
      if (fe.active) {
        fd.disabled = false;
      }
    });
    return orgs;
  }
  @computed get filteredCategories(): Array<FilterDisplay> {
    const cats: Array<FilterDisplay> = new Array<FilterDisplay>();
    this.filterEntries.map((fe: FilterEntry): void => {
      let fd: FilterDisplay = cats.find((fd: FilterDisplay): boolean => { return fd.value === fe.categoryId });
      if (!fd) {
        const cat: Category = this.getCategory(fe.categoryId);
        if (cat) {
          fd = new FilterDisplay(this.getCategory(fe.categoryId).name, fe.categoryId);
          fd.checked = this.filterRules.categories.length > 0 ? this.filterRules.categories.indexOf(fe.categoryId) !== -1 : false;
          cats.push(fd);
        }
      }
      if (fe.active && fd) {
        fd.disabled = false;
      }
    });
    return cats;
  }
  @computed get filteredLevels(): Array<FilterDisplay> {
    const lvls: Array<FilterDisplay> = new Array<FilterDisplay>();
    this._store.localization.fields.addJob.expLevel.map((lvl: string, i: number): void => {
      if (i === 0) { return; }
      let fld: FilterDisplay = new FilterDisplay(lvl, i);
      fld.checked = this.filterRules.levels.indexOf(i) !== -1;
      fld.disabled = this.filterEntries.findIndex((f: FilterEntry): boolean => { return f.level === i && f.active }) === -1;
      lvls.push(fld);
    });
    return lvls;
  }
  @computed get filteredEmploymentType(): Array<FilterDisplay> {
    const lvls: Array<FilterDisplay> = new Array<FilterDisplay>();
    this._store.localization.getLookup(LookupType.PositionType).map((lv: LookupValue): void => {
      let fld: FilterDisplay = new FilterDisplay(lv.value, lv.order);
      fld.checked = this.filterRules.empTypes.indexOf(lv.order) !== -1;
      fld.disabled = this.filterEntries.findIndex((f: FilterEntry): boolean => { return f.employmentType === lv.order && f.active }) === -1;
      lvls.push(fld);
    });
    return lvls;
  }
  @computed get filterdEdLevels(): Array<FilterDisplay> {
    const lvls: Array<FilterDisplay> = new Array<FilterDisplay>();
    let fld: FilterDisplay = new FilterDisplay(this._store.localization.fields.filterComponent.edUnlimited, 0);
    fld.checked = this.filterRules.minEd === 0;
    fld.disabled = false;
    lvls.push(fld);
    this._store.localization.getLookup(LookupType.EducationLevel).map((lv: LookupValue): void => {
      if (lv.order === 2) {
        lvls[1].label += `/${lv.value}`;
      } else {
        let fld: FilterDisplay = new FilterDisplay(lv.value, lv.order);
        fld.checked = this.filterRules.minEd <= 2 ? lv.order <= this.filterRules.minEd : lv.order === this.filterRules.minEd;
        fld.disabled = this.filterEntries.findIndex((f: FilterEntry): boolean => { return f.minEd >= lv.order && f.active }) === -1;
        lvls.push(fld);
      }
    });
    return lvls;
  }

  getRecommendedOps(): void {
    if (this._store.profileStore.profile && this.recommendedOps.length === 0) {
      this._store.viewState.setBusyBit(BusyEvent.LoadRecommendedOps)
      const request: RecommendedOpRequest = new RecommendedOpRequest(this._store.profileStore.profile);
      Fetcher.postWithData(`api/opportunities/recommended?count=6`, request, action((ops: Array<OpportunitySummary>): void => {
        copyArray(ops, this.recommendedOps, (os: OpportunitySummary) => { return new OpportunitySummary(os); });
        this._store.viewState.clearBusyBit(BusyEvent.LoadRecommendedOps);
      }));
    } 
  }

  updateRecommendedOps(): void {
    let prev: Array<String> = [];
    if (this.recommendedOps.length > 0) { prev = this.recommendedOps.map(function (x) { return x.idHash; }).sort(); }
    if (this._store.profileStore.profile) {
      this._store.viewState.setBusyBit(BusyEvent.LoadRecommendedOps)
      const request: RecommendedOpRequest = new RecommendedOpRequest(this._store.profileStore.profile);
      Fetcher.postWithData(`api/opportunities/recommended?count=6`, request, action((ops: Array<OpportunitySummary>): void => {
        copyArray(ops, this.recommendedOps, (os: OpportunitySummary) => { return new OpportunitySummary(os); });
        if (this.recommendedOps.length > 0) {
          const next: Array<String> = this.recommendedOps.map(function (x) { return x.idHash }).sort();
          if (prev.join(',') !== next.join(',')) {
            this._store.viewState.setShowSnackbar(true, "You have new Recommended Opportunities", true, "View Opportunities", "/home");
          }
        }
        this._store.viewState.clearBusyBit(BusyEvent.LoadRecommendedOps);
      }));
    }
  }
 
  // application process
  initApplication = (op?: Opportunity): void => {
    op = op || this.selectedOp;
    if (!op || !this._store.profileStore.profile) {
      return;
    }
    const savedOp: SavedOpportunity = this._store.profileStore.getSavedOpportunity(op.idHash);
    if (!savedOp) { return;}
    this.application = new Application();
    this.application.appProcess = op.appProcess;
    this.application.opId = op.idHash;
    this.application.mapFromProfile(this._store.profileStore.profile.demographics);
    op.requiredSkills().map((sk: SetSkill): void => {
      const sf: SkillFit = savedOp.skillFit.find((f: SkillFit): boolean => { return f.skill === sk.skillId });
      this.application.scores.push({ skill: sk.skillId, score: sf ? sf.level : 0 });
    });
    op.requiredSkills().map((sk: SetSkill): void => {
      const sf: SkillFit = savedOp.skillFit.find((f: SkillFit): boolean => { return f.skill === sk.skillId });
      this.application.scores.push({ skill: sk.skillId, score: sf ? sf.level : 0 });
    });
    this.application.score = savedOp.fit;
  }
  applyForOpportunity = (): void => {
    if (!this.application) { return; }
    this._store.viewState.setBusyBit(BusyEvent.SubmitApplication);
    Fetcher.putWithResult(`api/opportunity/application/${this.application.opId}`, this.application, action((resp: IHttpResult): void => {
      if (resp.ok) {
        this._store.viewState.setShowSnackbar(true, this._store.localization.fields.applyDlg.submitMsg);
        this._store.profileStore.updateSavedOpportunityState(this.application.opId, SavedOpportunityState.Applied, true);
        if (this.application.appProcess === ApplicationProcess.EmailProfile) {
          hashHistory.push(`opportunities/${this.application.opId}`);
        }
      }
      this._store.viewState.clearBusyBit(BusyEvent.SubmitApplication);
      this.application = null;
    }));
  }
  @action updateApplication = (key: string, val: any): void => {
    if (this.application) {
      this.application[key] = val;
    }
  }
  nullApply = (): void => {

  }
  externalApply = (op?: Opportunity): void => {
    op = op || this.selectedOp;
    if (!op) { return; }  
    this.initApplication(op);
    this.applyForOpportunity();
    this._store.viewState.setShowExternalLink(true, op.extApplicationUrl);
  }
  internalApply = (op?: Opportunity): void => {
    op = op || this.selectedOp;
    if (!op) { return; }
    this.initApplication(op);
    hashHistory.push(`opportunities/${op.idHash}/apply`);
  }
  getApplicationFunction = (op?: Opportunity): () => void => {  
    op = op || this.selectedOp;  
    if (!op || op.appProcess === ApplicationProcess.Unspecified) {
      return this.nullApply;
    } else if (op.appProcess === ApplicationProcess.EmailProfile) {
      return this.internalApply;
    } else {
      return this.externalApply;
    }
  }
}


// WEBPACK FOOTER //
// ./src/stores/opportunityStore.ts